import type {PluginContext} from '@jetbrains/teamcity-ui'
import {baseUri} from '@jetbrains/teamcity-ui'
import Plugin, {PlaceId} from '@jetbrains/teamcity-ui/dist/plugin'
import pluginRegistry from '@jetbrains/teamcity-ui/dist/plugin/registry'
import type {WritableKeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import {useEffect} from 'react'
import type {$PropertyType} from 'utility-types'

import {BS} from '../../../types/BS_types'
import {noop} from '../../../utils/empty'
import {getScripts, withoutScripts} from '../InjectHtml/InjectHtml.utils'

import {pluginsApi} from './Plugin.requests'
import type {DummyPlugin} from './Plugin.types'

type PluginLocationContext = $PropertyType<PluginContext, 'location'>
const cssFilesCache: WritableKeyValue<string, boolean> = {}

const loadCssFiles = (
  paths: Array<string>,
  options: {
    name: string
  },
) => {
  const links = paths.map(path => {
    cssFilesCache[path] = true
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.type = 'text/css'
    link.href = `${baseUri}/${path}`
    link.dataset.plugin = options.name
    document.getElementsByTagName('HEAD')[0].appendChild(link)

    return link
  })

  return links.filter(Boolean)
}

const prepareExternalJS = (paths: Array<string>) =>
  paths.map(path => {
    const script = document.createElement('script')
    script.src = `${baseUri}/${path}`
    return script
  })

type OwnProps = {
  availablePlugins: ReadonlyArray<DummyPlugin>
  activeEntities: PluginLocationContext
  pluginDevelopmentMode: string | null | undefined
  availablePluginsLoading: boolean
  placeId: PlaceId
}
export const usePluginsLoader = ({
  availablePlugins,
  activeEntities,
  placeId,
  pluginDevelopmentMode,
  availablePluginsLoading,
}: OwnProps) => {
  const [getPluginContent] = pluginsApi.endpoints.getPluginContent.useLazyQuerySubscription()
  useEffect(() => {
    if (availablePluginsLoading) {
      return noop
    }

    const updatePluginsCallbackList = availablePlugins.map(plugin => {
      let pluginInstance: Plugin | null = null
      let isCancelled = false
      let scriptsContainer: HTMLElement | null | undefined = null
      let cssLinks: Array<HTMLLinkElement | null> = []
      const cachedPlugin = pluginRegistry.searchByPlaceId(placeId, plugin.name)

      if (cachedPlugin instanceof Plugin && cachedPlugin.isControlled()) {
        return noop
      }

      const loadContent = async () => {
        const result = await getPluginContent({pluginUrl: plugin.extensionUrl, activeEntities})

        if (!BS || isCancelled || 'error' in result) {
          return
        }

        const content = result.data

        const scripts = getScripts(content)
        pluginInstance = new Plugin(placeId, {
          content: withoutScripts(content),
          name: plugin.name,
          options: {
            debug: pluginDevelopmentMode != null,
          },
          onMount: () => {
            cssLinks = loadCssFiles(plugin.css, {name: plugin.name})

            const js = prepareExternalJS(plugin.js)
            const range = document.createRange()
            scriptsContainer = document.createElement('div')
            scriptsContainer.dataset.tcPluginScripts = `${placeId}-${plugin.name}`
            range.selectNodeContents(scriptsContainer)
            range.deleteContents()
            range.setStart(scriptsContainer, 0)

            if (scripts != null) {
              scriptsContainer.appendChild(range.createContextualFragment(scripts))
            }

            js.map(script => scriptsContainer?.appendChild(script))
            document.body?.appendChild(scriptsContainer)
            return noop
          },
        })
      }

      loadContent()
      return () => {
        isCancelled = true

        if (pluginInstance == null) {
          scriptsContainer?.remove()
          return
        }

        if (!pluginInstance.isControlled() || placeId === PlaceId.TAB_PLUGIN_CONTAINER) {
          ;[...cssLinks].forEach(link => link?.remove())
          scriptsContainer?.remove()
          pluginInstance?.remove()
        }
      }
    })
    return () =>
      updatePluginsCallbackList.forEach(
        updatePlugin => typeof updatePlugin === 'function' && updatePlugin(),
      )
  }, [
    activeEntities,
    availablePlugins,
    availablePluginsLoading,
    getPluginContent,
    placeId,
    pluginDevelopmentMode,
  ])
}
